import { action, observable, computed } from 'mobx';
import { FeatureFlag, FeatureSection } from 'types';

import { getAllFeatures } from '../services/ConfigService';
import type { IRootStore } from './IRootStore';

export default class FeaturesStore {
  rootStore: IRootStore;

  @observable standardFeaturesFetched = false;

  @observable featuresFlags: FeatureSection[] = [];

  @observable enablementOverrides: Record<string, boolean> = {};

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
  }

  @computed get allFeaturesFetched(): boolean {
    return (
      this.standardFeaturesFetched &&
      this.rootStore.userStore.didFetchEnablements
    );
  }

  @computed get features(): FeatureSection[] {
    let enablementFeaturesFlags: FeatureSection[] = [];
    const enablementFeatures =
      this.rootStore.userStore.enablementsFeatures || {};
    const enablementNames = new Set([
      ...Object.keys(enablementFeatures),
      ...Object.keys(this.enablementOverrides),
    ]);
    const flags: FeatureFlag[] = [];

    enablementNames.forEach((name) => {
      const hasOverride = Object.prototype.hasOwnProperty.call(
        this.enablementOverrides,
        name,
      );
      const active = hasOverride
        ? this.enablementOverrides[name]
        : Boolean(enablementFeatures[name]);
      flags.push({ name, active });
    });

    if (flags.length > 0) {
      enablementFeaturesFlags = [{ section: 'enablements', flags }];
    }

    const legacyFeatures = this.featuresFlags.filter(
      (feature) => feature.section !== 'enablements',
    );

    return [...enablementFeaturesFlags, ...legacyFeatures];
  }

  getFeatureFlag = (section: string, name: string): FeatureFlag => {
    const foundSection = this.features.find((f) => f.section === section);
    let feature = null;
    if (foundSection && Array.isArray(foundSection.flags)) {
      feature = foundSection.flags.find((flag) => flag.name === name);
    }
    return feature;
  };

  isFeatureEnabled = (
    section: string,
    name: string,
    defaultValue?: boolean,
  ) => {
    const featureFlag = this.getFeatureFlag(section, name);
    return this.isAFeatureFlagEnabled(featureFlag, defaultValue);
  };

  @action setAFeature = (feature: FeatureSection) => {
    if (feature.section === 'enablements') {
      this.setEnablementOverrides(feature);
      return;
    }

    const indexOfFeature = this.featuresFlags.findIndex(
      (f) => f.section === feature.section,
    );

    if (indexOfFeature >= 0) {
      this.featuresFlags[indexOfFeature] = feature;
    } else {
      this.featuresFlags.push(feature);
    }
  };

  @action setEnablementOverrides = (feature: FeatureSection) => {
    const overrides: Record<string, boolean> = {};
    feature.flags.forEach((flag) => {
      overrides[flag.name] = flag.active;
    });

    this.enablementOverrides = overrides;
  };

  @action clearEnablementOverrides = () => {
    this.enablementOverrides = {};
  };

  @action isAFeatureFlagEnabled = (
    featureFlag: FeatureFlag,
    defaultFlagValue: boolean = false,
  ): boolean => {
    let isFeatureEnabled = null;

    if (featureFlag) {
      const hasRole = this.rootStore.userStore.hasRoles(
        featureFlag?.super_roles || [],
      );
      const hasSubscription = this.hasSubscription(
        featureFlag?.super_subscriptions,
      );
      const hasAUserName = this.hasAUserName(featureFlag?.super_users);

      isFeatureEnabled =
        hasRole || hasSubscription || hasAUserName || featureFlag.active;
    }

    return isFeatureEnabled !== null ? isFeatureEnabled : defaultFlagValue;
  };

  @action findIsFeatureEnabledBySectionFlagName = (
    section: string,
    flagName: string,
    defaultFlagValue: boolean = false,
  ): boolean => {
    const flag = this.features
      ?.find((f) => f.section?.toLowerCase() === section?.toLowerCase())
      ?.flags.find((f) => f.name?.toLowerCase() === flagName?.toLowerCase());
    if (flag) {
      return this.isAFeatureFlagEnabled(flag, defaultFlagValue);
    }
    return defaultFlagValue;
  };

  /* eslint-disable no-param-reassign */
  @action hasSubscription(subscriptionCodes) {
    if (subscriptionCodes && !Array.isArray(subscriptionCodes)) {
      subscriptionCodes = [subscriptionCodes];
    }
    const currentUserSubscription =
      this.rootStore.userStore.currentSubscriptionCode?.toLowerCase()?.trim();

    if (!currentUserSubscription) {
      return (
        this.rootStore.userStore.isAlaCarte &&
        subscriptionCodes?.find(
          (code) => code?.toLowerCase()?.trim() === 'alacarte',
        )
      );
    }

    return (
      subscriptionCodes?.find(
        (code) => code?.toLowerCase()?.trim() === currentUserSubscription,
      ) !== undefined
    );
  }

  /* eslint-disable no-param-reassign */
  @action hasAUserName(usernames) {
    if (usernames && !Array.isArray(usernames)) {
      usernames = [usernames];
    }
    const currentUserName = this.rootStore.userStore.username
      ?.toLowerCase()
      ?.trim();
    return (
      usernames?.find(
        (username) => username?.toLowerCase()?.trim() === currentUserName,
      ) !== undefined
    );
  }

  @action getAllFeatures = async () => {
    try {
      const allFeatures = await getAllFeatures();
      this.featuresFlags = [];
      this.clearEnablementOverrides();

      allFeatures?.forEach((feature) => {
        this.setAFeature(feature);
      });
    } catch {
      this.featuresFlags = [];
      this.clearEnablementOverrides();
    } finally {
      this.standardFeaturesFetched = true;
    }
  };
}
